if (!customElements.get('custom-recharge-widget')) {
  customElements.define('custom-recharge-widget', class CustomRechargeWidget extends HTMLElement {
    constructor() {
      super();

      /** InsCoder WANG.J 2026-08-13 B + Default custom Recharge widget to One-Time Purchase **/
      this.mode = 'onetime';
      /** InsCoder WANG.J 2026-08-13 E + Default custom Recharge widget to One-Time Purchase **/
      this.plans = [];
      this.selectedPlanId = null;
      this.boundOutsideClick = this.handleOutsideClick.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      this.form = this.closest('form');
      this.variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
      this.sellingPlanInput = this.form ? this.form.querySelector('[name="items[0][selling_plan]"]') : null;
      this.itemIdInput = this.form ? this.form.querySelector('[name="items[0][id]"]') : null;
      this.itemQuantityInput = this.form ? this.form.querySelector('[name="items[0][quantity]"]') : null;
      this.planSelect = this.querySelector('[data-plan-select]');
      this.popup = this.querySelector('[data-popup]');
      this.popupToggle = this.querySelector('[data-popup-toggle]');
      this.subscriptionOption = this.querySelector('[data-option="subscription"]');
      this.onetimeOption = this.querySelector('[data-option="onetime"]');
      this.variantData = this.parseVariantData();
      this.selectedVariantId = this.getCurrentVariantId();

      this.bindEvents();
      document.addEventListener('click', this.boundOutsideClick);
      document.addEventListener('keydown', this.boundKeydown);
      this.hideLegacyRechargeWidget();
      this.init();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.boundOutsideClick);
      document.removeEventListener('keydown', this.boundKeydown);

      if (this.variantObserver) {
        this.variantObserver.disconnect();
      }
    }

    async init() {
      if (!this.form || !this.sellingPlanInput || !this.planSelect || !this.variantData.length) {
        this.hideWidget();
        return;
      }

      const rechargeReady = await this.waitForRecharge();

      if (!rechargeReady) {
        this.hideWidget();
        return;
      }

      try {
        if (!window.rechargeSession) {
          window.rechargeSession = await recharge.auth.loginShopifyAppProxy();
        }

        const response = await recharge.plan.listPlans(window.rechargeSession, {
          external_product_id: this.dataset.productId
        });

        this.plans = (response && response.plans ? response.plans : [])
          .filter((plan) => plan.type === 'subscription')
          .filter((plan) => this.isDisplayablePlan(plan))
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

        this.renderForVariant();
        this.hideLegacyRechargeWidget();
      } catch (error) {
        this.hideWidget();
      }
    }

    bindEvents() {
      this.subscriptionOption.addEventListener('click', () => {
        this.setMode('subscription');
      });

      this.onetimeOption.addEventListener('click', () => {
        this.setMode('onetime');
      });

      this.planSelect.addEventListener('click', (event) => {
        event.stopPropagation();
      });

      this.planSelect.addEventListener('change', () => {
        this.selectedPlanId = this.planSelect.value;
        this.setMode('subscription');
      });

      this.popupToggle.addEventListener('click', (event) => {
        event.stopPropagation();
        this.togglePopup();
      });

      const closeButton = this.querySelector('[data-popup-close]');

      if (closeButton) {
        closeButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.closePopup();
        });
      }

      if (this.variantInput) {
        this.variantInput.addEventListener('change', () => {
          this.handleVariantChange();
        });

        this.variantObserver = new MutationObserver(() => {
          this.handleVariantChange();
        });

        this.variantObserver.observe(this.variantInput, {
          attributes: true,
          attributeFilter: ['value']
        });
      }
    }

    waitForRecharge() {
      return new Promise((resolve) => {
        let attempts = 0;
        const interval = window.setInterval(() => {
          attempts += 1;

          if (window.recharge && recharge.auth && recharge.plan && recharge.plan.listPlans) {
            window.clearInterval(interval);
            resolve(true);
          }

          if (attempts >= 100) {
            window.clearInterval(interval);
            resolve(false);
          }
        }, 100);
      });
    }

    parseVariantData() {
      const json = this.querySelector('[data-custom-recharge-variants]');

      if (!json) {
        return [];
      }

      try {
        return JSON.parse(json.textContent);
      } catch (error) {
        return [];
      }
    }

    getCurrentVariantId() {
      if (this.variantInput && this.variantInput.value) {
        return this.variantInput.value;
      }

      return this.dataset.defaultVariantId;
    }

    handleVariantChange() {
      const nextVariantId = this.getCurrentVariantId();

      if (String(nextVariantId) === String(this.selectedVariantId)) {
        return;
      }

      this.selectedVariantId = nextVariantId;
      this.renderForVariant();
    }

    getSelectedVariant() {
      return this.variantData.find((variant) => String(variant.id) === String(this.selectedVariantId));
    }

    getEligiblePlans(variant) {
      if (!variant || !Array.isArray(variant.selling_plan_allocations)) {
        return [];
      }

      const allocationIds = variant.selling_plan_allocations.map((allocation) => String(allocation.selling_plan_id));

      return this.plans.filter((plan) => allocationIds.includes(String(plan.external_plan_id)));
    }

    isDisplayablePlan(plan) {
      const channelSettings = plan.channel_settings;

      if (!channelSettings) {
        return true;
      }

      return Boolean(channelSettings.api && channelSettings.api.display)
        || Boolean(channelSettings.checkout_page && channelSettings.checkout_page.display);
    }

    getAllocation(variant, planId) {
      if (!variant || !Array.isArray(variant.selling_plan_allocations)) {
        return null;
      }

      return variant.selling_plan_allocations.find((allocation) => String(allocation.selling_plan_id) === String(planId));
    }

    renderForVariant() {
      const variant = this.getSelectedVariant();
      const eligiblePlans = this.getEligiblePlans(variant);

      if (!variant || !eligiblePlans.length) {
        if (variant) {
          this.updateButtonPrice(variant.price);
        }

        this.hideWidget();
        return;
      }

      this.renderPlanOptions(eligiblePlans);

      if (!this.selectedPlanId || !eligiblePlans.some((plan) => String(plan.external_plan_id) === String(this.selectedPlanId))) {
        this.selectedPlanId = eligiblePlans[0].external_plan_id;
      }

      this.planSelect.value = this.selectedPlanId;
      this.hidden = false;
      this.updateDisplay(variant);
      this.applyFormState(variant);
    }

    renderPlanOptions(plans) {
      const optionsMarkup = plans.map((plan) => {
        const title = this.escapeHtml(this.getSelectedPlanLabel(plan));

        return `<option value="${this.escapeHtml(plan.external_plan_id)}">${title}</option>`;
      }).join('');

      this.planSelect.innerHTML = optionsMarkup;
    }

    getPlanLabel(plan) {
      return String(plan.title || plan.external_plan_name || plan.name || 'Subscription');
    }

    getSelectedPlanLabel(plan) {
      return this.getPlanLabel(plan).replace(/^ships? every\s+/i, '');
    }

    setMode(mode) {
      if (mode === 'subscription' && !this.selectedPlanId) {
        return;
      }

      this.mode = mode;
      const variant = this.getSelectedVariant();

      this.subscriptionOption.classList.toggle('is-active', this.mode === 'subscription');
      this.subscriptionOption.setAttribute('aria-pressed', this.mode === 'subscription' ? 'true' : 'false');
      this.onetimeOption.classList.toggle('is-active', this.mode === 'onetime');
      this.onetimeOption.setAttribute('aria-pressed', this.mode === 'onetime' ? 'true' : 'false');

      this.updateDisplay(variant);
      this.applyFormState(variant);
    }

    updateDisplay(variant) {
      const allocation = this.getAllocation(variant, this.selectedPlanId);

      if (!variant || !allocation) {
        return;
      }

      const oneTimePrice = Number(variant.price);
      const subscriptionPrice = Number(allocation.price);
      const comparePrice = Math.max(Number(allocation.compare_at_price || 0), oneTimePrice);
      this.querySelector('[data-subscription-compare]').textContent = this.formatMoney(comparePrice);
      this.querySelector('[data-subscription-price]').textContent = this.formatMoney(subscriptionPrice);
      /** InsCoder WANG.J 2026-08-13 B + Display the full One-Time price with two decimals **/
      this.querySelector('[data-onetime-price]').textContent = this.formatMoney(oneTimePrice, true);
      /** InsCoder WANG.J 2026-08-13 E + Display the full One-Time price with two decimals **/

      this.updateButtonPrice(this.mode === 'subscription' ? subscriptionPrice : oneTimePrice);
    }

    applyFormState(variant) {
      const allocation = this.getAllocation(variant, this.selectedPlanId);

      if (this.mode === 'subscription' && allocation) {
        this.sellingPlanInput.value = this.selectedPlanId;
        this.sellingPlanInput.disabled = false;
        this.setTopLevelVariantDisabled(true, variant);
        this.setItemFieldsDisabled(false, variant);
      } else {
        this.sellingPlanInput.disabled = true;
        this.setTopLevelVariantDisabled(false, variant);
        this.setItemFieldsDisabled(true, variant);
      }

      this.sellingPlanInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    setItemFieldsDisabled(disabled, variant) {
      if (this.itemIdInput) {
        if (variant && variant.id) {
          this.itemIdInput.value = variant.id;
        }

        this.itemIdInput.disabled = disabled;
      }

      if (this.itemQuantityInput) {
        this.itemQuantityInput.disabled = disabled;
      }
    }

    setTopLevelVariantDisabled(disabled, variant) {
      if (!this.variantInput) {
        return;
      }

      if (variant && variant.id) {
        this.variantInput.value = variant.id;
      }

      this.variantInput.disabled = disabled;
    }

    updateButtonPrice(price) {
      const productForm = this.closest('product-form');
      const buttonPrice = productForm ? productForm.querySelector('.product-form__buttons .block--price') : null;

      if (buttonPrice) {
        buttonPrice.textContent = this.formatMoney(price);
      }
    }

    formatDifference(value) {
      const sign = value >= 0 ? '+' : '-';

      return `${sign}${this.formatMoney(Math.abs(value))}`;
    }

    formatMoney(cents, keepDecimals = false) {
      const numericCents = Number(cents || 0);
      let formatted = '';
      const activeMoneyFormat = window.ShopifyCurrency && window.ShopifyCurrency.moneyFormat
        ? window.ShopifyCurrency.moneyFormat
        : this.dataset.moneyFormat;
      const moneyFormat = keepDecimals
        ? this.getTwoDecimalMoneyFormat(activeMoneyFormat)
        : activeMoneyFormat;

      if (keepDecimals && window.Shopify && typeof window.Shopify.formatMoney === 'function' && moneyFormat) {
        formatted = window.Shopify.formatMoney(numericCents, moneyFormat);
      } else if (typeof window.formatMoney === 'function') {
        formatted = window.formatMoney(numericCents);
      } else if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        formatted = window.Shopify.formatMoney(numericCents, moneyFormat);
      } else {
        formatted = `$${(numericCents / 100).toFixed(2)}`;
      }

      return keepDecimals ? formatted : formatted.replace(/([.,]00)(?!\d)/, '');
    }

    /** InsCoder WANG.J 2026-08-13 B + Preserve active market currency while forcing two decimals **/
    getTwoDecimalMoneyFormat(format) {
      return String(format || '')
        .replace(/amount_no_decimals_with_comma_separator/g, 'amount_with_comma_separator')
        .replace(/amount_no_decimals/g, 'amount');
    }
    /** InsCoder WANG.J 2026-08-13 E + Preserve active market currency while forcing two decimals **/

    togglePopup() {
      if (this.popup.hidden) {
        this.openPopup();
      } else {
        this.closePopup();
      }
    }

    openPopup() {
      this.popup.hidden = false;
      this.popupToggle.setAttribute('aria-expanded', 'true');
    }

    closePopup() {
      this.popup.hidden = true;
      this.popupToggle.setAttribute('aria-expanded', 'false');
    }

    handleOutsideClick(event) {
      if (!this.contains(event.target)) {
        this.closePopup();
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        this.closePopup();
      }
    }

    hideWidget() {
      this.hidden = true;
      this.closePopup();

      if (this.sellingPlanInput) {
        this.sellingPlanInput.disabled = true;
      }

      this.setTopLevelVariantDisabled(false, null);
      this.setItemFieldsDisabled(true, null);
    }

    hideLegacyRechargeWidget() {
      document.querySelectorAll('.rc-container-wrapper, .rc_container_wrapper').forEach((container) => {
        container.hidden = true;
        container.setAttribute('aria-hidden', 'true');
      });
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }
  });
}
